import { useEffect, useState } from "react";
import { Loader2, Save, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui-kit/Button";
import { Input, Label } from "@/components/ui-kit/Input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui-kit/Card";
import { useFunder } from "@/funder/FunderContext";

function FirmBrandingEditor() {
  const { refresh } = useFunder();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/settings").then((r) => setForm({
      firm_name: r.data.firm_name || "", firm_address: r.data.firm_address || "",
      firm_phone: r.data.firm_phone || "", firm_email: r.data.firm_email || "",
      firm_logo: r.data.firm_logo || "", remittance: r.data.remittance || "",
      invoice_footer: r.data.invoice_footer || "", itemization_default: !!r.data.itemization_default,
    })).catch(() => setForm({ firm_name: "", firm_address: "", firm_phone: "", firm_email: "", firm_logo: "", remittance: "", invoice_footer: "", itemization_default: false }));
  }, []);

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) { setError("Logo must be under 500 KB."); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, firm_logo: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true); setError("");
    try {
      await api.put("/settings", form);
      await refresh();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  if (form === null) return <div className="grid place-items-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;

  const ta = "w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40";
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-4" data-testid="branding-editor">
      <div className="flex items-center gap-4">
        <div className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-md border border-border bg-secondary">
          {form.firm_logo ? <img src={form.firm_logo} alt="logo" className="max-h-16 max-w-16 object-contain" data-testid="logo-preview" /> : <span className="text-[10px] text-muted-foreground">No logo</span>}
        </div>
        <div className="space-y-1.5">
          <input type="file" accept="image/*" onChange={onLogo} data-testid="logo-upload" className="block text-sm text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-1.5 file:text-sm file:text-foreground hover:file:bg-secondary/70" />
          {form.firm_logo && <button type="button" onClick={() => setForm({ ...form, firm_logo: "" })} data-testid="logo-remove" className="text-xs text-muted-foreground hover:text-destructive">Remove logo</button>}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div><Label htmlFor="firm-name">Firm name</Label><Input id="firm-name" data-testid="firm-name-input" value={form.firm_name} onChange={set("firm_name")} placeholder="Meridian Legal Group" /></div>
        <div><Label htmlFor="firm-email">Email</Label><Input id="firm-email" data-testid="firm-email-input" value={form.firm_email} onChange={set("firm_email")} placeholder="billing@firm.com" /></div>
        <div><Label htmlFor="firm-phone">Phone</Label><Input id="firm-phone" data-testid="firm-phone-input" value={form.firm_phone} onChange={set("firm_phone")} placeholder="(555) 123-4567" /></div>
        <div>
          <Label htmlFor="firm-address">Address</Label>
          <textarea id="firm-address" data-testid="firm-address-input" value={form.firm_address} onChange={set("firm_address")} rows={2} className={ta} placeholder="123 Main St, Suite 400" />
        </div>
      </div>

      <div>
        <Label htmlFor="firm-remit">Remittance / payment instructions</Label>
        <textarea id="firm-remit" data-testid="firm-remittance-input" value={form.remittance} onChange={set("remittance")} rows={2} className={ta} placeholder="Make checks payable to… / wire details…" />
      </div>
      <div>
        <Label htmlFor="firm-footer">Invoice footer / terms</Label>
        <textarea id="firm-footer" data-testid="firm-footer-input" value={form.invoice_footer} onChange={set("invoice_footer")} rows={2} className={ta} placeholder="Payment due within 30 days. Thank you." />
      </div>

      <label className="flex items-center gap-2 text-sm text-foreground">
        <input type="checkbox" checked={form.itemization_default} onChange={(e) => setForm({ ...form, itemization_default: e.target.checked })} data-testid="itemization-default-toggle" className="h-4 w-4 accent-primary" />
        Include the itemized time appendix on invoices by default
      </label>

      <div className="flex items-center gap-3">
        {saved && <span className="inline-flex items-center gap-1 text-sm text-status-paid"><Check className="h-4 w-4" /> Saved</span>}
        {error && <span className="text-sm text-destructive">{error}</span>}
        <div className="ml-auto">
          <Button size="sm" onClick={save} disabled={saving} data-testid="branding-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save branding
          </Button>
        </div>
      </div>
    </div>
  );
}

export function FirmBrandingCard() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Firm branding</CardTitle>
        <CardDescription>Appears as the letterhead and footer on every generated invoice and statement PDF.</CardDescription>
      </CardHeader>
      <CardContent><FirmBrandingEditor /></CardContent>
    </Card>
  );
}
